import { FullHeightContainer } from "../core/components/FullHeighContainer";
import { Helper } from "../core/util/helper";
import { HTTPService, JsonResultClass } from "../core/util/httpManager";
import { SimpleRadioButton } from "../core/components/simpleRadioButton";
import { General } from "../../siteManagement/general";
import { SheetEventType } from "../../siteManagement/sheet";
import { IntKeyDictionary, StringKeyDictionary } from "../commonInterfaces";
import React from "react";

export default class UserModuleAllocation extends React.Component<UserModuleAllocationProps, UserModuleAllocationState> {
    private _userName: string = null;


    constructor(props: UserModuleAllocationProps) {
        super(props);

        this.state = {
            package: null,
            lastSaveTimestamp: null,
            licenseUsage: null
        };

        this._userName = props.entity.User.Name;
    }


    private _getData = (timestamp: number) => {
        HTTPService.get<JsonResultClass<CurrentLicensePackageInformation>>("Admin/GetLicenseInformation")
            .then(result => {
                const usage: IntKeyDictionary<number> = {};
                result.Data.ContainedLicenses.forEach(lic => {
                    usage[lic.Id] = Array.isArray(this.props.entity.ModuleRights.ActiveModules) && this.props.entity.ModuleRights.ActiveModules.indexOf(lic.Id) !== -1 ? 1 : 0;
                });

                this.setState({ package: result.Data, lastSaveTimestamp: timestamp, licenseUsage: usage });
            });
    }

    private _onModuleStatusChanged = (modules: Array<LicenseActivationStatus>) => {
        this.props.onChange({ model: "entity.ModuleRights.ActiveModules" }, modules.filter(x => x.activationStatus === ActivationStatus.On).map(x => x.id), modules);
    }


    componentDidMount() {
        General.Sheet.Manager.getTopMostSheet().bind(SheetEventType.SavedSuccessfully, () => {
            this._getData(Date.now());
        });
        this._getData(null);
    }

    componentDidUpdate(prevProps: UserModuleAllocationProps) {
        if (this._userName !== this.props.entity.User.Name) {
            this._userName = this.props.entity.User.Name;
            this._getData(null);
        }
    }

    render() {
        const model: Array<LicenseActivationStatus> = this.props.entity.ModuleRights.ActiveModules.map(id => { return { id: id, activationStatus: ActivationStatus.On } });

        return (
            <FullHeightContainer style={{ padding: "10px" }}>
                {
                    this.state.package ?
                        <div>
                            <ModuleLicenseTable
                                package={this.state.package}
                                onChange={this._onModuleStatusChanged}
                                model={model}
                                numberOfUsers={1}
                                licenseUsage={this.state.licenseUsage}
                                lastSaveTimestamp={this.state.lastSaveTimestamp}
                                readonly={this.props.entity.RecordRight.CanWrite !== true}
                                translations={{
                                    expiredLicenses: this.props.translations["WebSMS.ModuleConfiguration.ExpiredLicenses"],
                                    on: this.props.translations["WebSMS.ModuleConfiguration.On"],
                                    off: this.props.translations["WebSMS.ModuleConfiguration.Off"],
                                }}
                            />
                        </div>
                        : null
                }
            </FullHeightContainer>
        );
    }
}

const dependantOnBase = [15, 20, 25, 30, 40];
const baseLicenseId = 10;
const portalLicenseId = 50;
const mobileLicenseId = 60;
const deactivatedOnPortal = [10, 15, 20, 25, 30, 40, 60];

const findLicenseById = (licenses: Array<LicenseActivationStatus>, id: number) => {
    if (Array.isArray(licenses)) {
        return licenses.filter(l => l.id === id)[0] || null;
    }

    return null;
}

const licenseMangers: StringKeyDictionary<LicenseManager> = {};

class LicenseManager {
    private _licenses: Array<LicenseData> = [];
    private _licenseDict: IntKeyDictionary<LicenseData> = {}
    private _id = kendo.guid();
    private _userCount: number = 0;


    constructor(data: LicenseManagerInitData) {
        data.licenses.forEach(lic => {
            if (data.description[lic.Id]) {
                const description = data.description[lic.Id]
                this._licenses.push({
                    id: lic.Id,
                    activeStatus: description.activationStatus,
                    dirty: false,
                    initialStatus: description.activationStatus,
                    capacity: description.capacity,
                    assigned: description.allocations,
                    initialUsage: description.currentUsage,
                    usageChange: 0
                });

                this._licenseDict[lic.Id] = this._licenses[this._licenses.length - 1];
            }
        });

        this._userCount = data.userCount;

        licenseMangers[this._id] = this;
    }


    private _setBusinessRules = () => {
        if (this._licenseDict[baseLicenseId] !== undefined && this._licenseDict[baseLicenseId].activeStatus !== ActivationStatus.NotSet) {
            const baseIsActive = this._licenseDict[baseLicenseId].activeStatus === ActivationStatus.On;
            dependantOnBase.forEach(lId => {
                if (this._licenseDict[lId]) {
                    if (!baseIsActive) {
                        let willChange = false;
                        if (this._licenseDict[lId].activeStatus !== ActivationStatus.Off) {
                            willChange = true;
                        }
                        if (willChange) {
                            this._licenseDict[lId].dirty = true;
                        }
                        this._licenseDict[lId].activeStatus = ActivationStatus.Off;
                    }
                }
            });
        }

        if (this._licenseDict[portalLicenseId] !== undefined && this._licenseDict[portalLicenseId].activeStatus !== ActivationStatus.NotSet) {
            const portalIsActive = this._licenseDict[portalLicenseId].activeStatus === ActivationStatus.On;

            deactivatedOnPortal.forEach(lId => {
                if (this._licenseDict[lId]) {
                    if (portalIsActive) {
                        let willChange = false;
                        if (this._licenseDict[lId].activeStatus !== ActivationStatus.Off) {
                            willChange = true;
                        }
                        if (willChange) {
                            this._licenseDict[lId].dirty = true;
                        }
                        this._licenseDict[lId].activeStatus = ActivationStatus.Off;
                    }
                }
            });
        }
    }

    private _updateUsageCounts = () => {
        this._licenses.forEach(lic => {
            if (lic.dirty) {
                const notAllocatedUsers = this._userCount - lic.initialUsage;

                if (lic.activeStatus === ActivationStatus.On) {
                    lic.usageChange = notAllocatedUsers;
                } else {
                    lic.usageChange = lic.initialUsage === 0 ? 0 : -lic.initialUsage;
                }
            }
        });
    }


    getId = () => this._id;

    setLicenseStatus = (id: number, status: ActivationStatus) => {
        const lic = this._licenseDict[id];
        if (lic && lic.activeStatus !== status) {
            lic.activeStatus = status;
            lic.dirty = true;

            this._setBusinessRules();
            this._updateUsageCounts();

            return lic.activeStatus === status;
        }

        return false;
    }

    getLicenseStatus = (id: number) => {
        const lic = this._licenseDict[id];
        if (lic) {
            return lic.activeStatus;
        }

        return null;
    }

    getLicenses = () => {
        return this._licenses.slice(0);
    }

    isCurrentStateConflicted = (licenses: Array<{ id: number; status: ActivationStatus }>) => {
        return true;
    }

    getExceededLicenses = () => {
        return this._licenses.filter(lic => lic.assigned + lic.usageChange > lic.capacity).map(lic => lic.id);//.length > 0;
    }

    canActivateLicense = (id: number) => {
        const license = this._licenseDict[id];

        if (license) {            
            const potentialAllocations = this._userCount - license.initialUsage;
            let generalActivationRule = true;
            if (license.id == mobileLicenseId || license.id == portalLicenseId)
                generalActivationRule = license.initialStatus === ActivationStatus.On || (license.assigned + license.usageChange <= license.capacity && potentialAllocations + license.assigned <= license.capacity);

            if (deactivatedOnPortal.indexOf(license.id) != -1 && this._licenseDict[portalLicenseId] !== undefined && this._licenseDict[portalLicenseId].activeStatus === ActivationStatus.On) {
                return false;
            }
            else {
                if (license.id === baseLicenseId) {
                    return generalActivationRule;
                } else {
                    if (dependantOnBase.indexOf(license.id) !== -1) {
                        return this._licenseDict[baseLicenseId].activeStatus === ActivationStatus.On;
                    } else {
                        return generalActivationRule;
                    }
                }
            }
        }

        return false;
    }

    canDeactivateLicense = (id: number) => {
        const license = this._licenseDict[id];
        const canActivate = this.canActivateLicense(id);

        if (!canActivate && license.activeStatus === ActivationStatus.Off) {
            return false;
        }

        return true;
    }
}


interface LicenseManagerInitData {
    licenses: Array<ContainedLicense>;
    description: IntKeyDictionary<{
        activationStatus: ActivationStatus;
        currentUsage: number;
        capacity: number;
        allocations: number;
    }>;
    userCount: number;
}

interface LicenseData {
    id: number;
    activeStatus: ActivationStatus;
    initialStatus: ActivationStatus;
    dirty: boolean;
    initialUsage: number;
    usageChange: number;
    capacity: number;
    assigned: number;
}

export class ModuleLicenseTable extends React.Component<ModuleLicenseTableProps, ModuleLicenseTableState> {


    constructor(props: ModuleLicenseTableProps) {
        super(props);

        this.state = {
            lastSaveTimestamp: null,
            managerId: null,
            renderData: null
        };

    }

    private _onLicenseStatusChanged = (license: ContainedLicense, on: boolean) => {
        if (licenseMangers[this.state.managerId].setLicenseStatus(license.Id, on ? ActivationStatus.On : ActivationStatus.Off)) {
            const newValues: Array<LicenseActivationStatus> = [];
            licenseMangers[this.state.managerId].getLicenses().forEach(lic => {
                newValues.push({
                    activationStatus: lic.activeStatus,
                    id: lic.id
                });
            });

            this.props.onChange(newValues);
        }
    }

    private _isRadioButtonDisabled = (license: ContainedLicense, forOn: boolean) => {
        if (this.props.readonly === true) {
            return true;
        }

        const renderData = Helper.getArrayItem(this.state.renderData, "id", license.Id);
        if (forOn) {
            return !renderData.canActivate; //|| renderData.currentLoad + renderData.usageChange > renderData.count;
        } else {
            return !renderData.canDeactivate;
        }
    }

    private _renderLicense = (license: ContainedLicense) => {
        const rbCellStyle: React.CSSProperties = { padding: "2px 0", margin: "0 5px" };
        const renderData = Helper.getArrayItem(this.state.renderData, "id", license.Id);

        return (
            <div key={"license_" + license.Id} style={{ display: "grid", gridTemplateRows: "25px", gridTemplateColumns: "175px 55px 60px 85px 85px", color: license.Expired ? "red" : null }} >
                <div style={{ gridColumn: "1/2", gridRow: "1/2", display: "flex", alignItems: "center" }}
                    onMouseEnter={this.props.onLicenseMouseEnter ? _ => { this.props.onLicenseMouseEnter(license); } : null}
                    onMouseLeave={this.props.onLicenseMouseLeave ? _ => { this.props.onLicenseMouseLeave(license); } : null}
                    onClick={this.props.onLicenseClick ? _ => { this.props.onLicenseClick(license); } : null}
                >
                    <div> {license.Name}</div>
                    {
                        Helper.hasValue(license.ExpirationDate) ?
                            <div>({Helper.dates.toString(Helper.dates.toDate(license.ExpirationDate))})</div>
                            : null
                    }
                </div>
                <div style={{ gridColumn: "2/3", gridRow: "1/2", display: "flex", alignItems: "center" }}>
                    {
                        !license.Expired ?
                            <SimpleRadioButton
                                label={this.props.translations.on}
                                onChange={(e) => this._onLicenseStatusChanged(license, true)}
                                value={renderData.state === ActivationStatus.On}
                                name={"licLbl_" + license.Id + "_active"}
                                disabled={this._isRadioButtonDisabled(license, true)}
                            />
                            : null
                    }
                </div>
                <div style={{ gridColumn: "3/4", gridRow: "1/2", display: "flex", alignItems: "center" }}>
                    {
                        !license.Expired ?
                            <SimpleRadioButton
                                label={this.props.translations.off}
                                onChange={(e) => this._onLicenseStatusChanged(license, false)}
                                value={renderData.state === ActivationStatus.Off}
                                name={"licLbl_" + license.Id + "_active"}
                                disabled={this._isRadioButtonDisabled(license, false)}
                            />
                            : null
                    }
                </div>
                <div style={{ gridColumn: "4/5", gridRow: "1/2", display: "flex", alignItems: "center" }}>
                    {license.Allocations} / {license.Amount}
                </div>
                <div style={{ gridColumn: "5/6", gridRow: "1/2", verticalAlign: "middle", paddingRight: "10px", color: "blue", display: "flex", alignItems: "center" }}>
                    {
                        !license.Expired && renderData.usageChange !== 0 ?
                            `${license.Allocations + renderData.usageChange} / ${license.Amount}`
                            : null
                    }
                </div>
            </div>


            //<tr key={"license_" + license.Id} style={{ color: license.Expired ? "red" : null }}>

            //    <td style={rbCellStyle} >
                    
            //    </td>
            //    <td style={rbCellStyle}>
                    
            //    </td>
            //    <td style={{ verticalAlign: "middle", paddingRight: "10px" }}>
            //        
            //    </td>
            //    <td style={{ verticalAlign: "middle", paddingRight: "10px", color: "blue" }}>

            //    </td>
            //</tr>
        );
    }


    static getDerivedStateFromProps(props: ModuleLicenseTableProps, state: ModuleLicenseTableState): ModuleLicenseTableState {
        let lastSaveTimestamp = state.lastSaveTimestamp;
        let createManager = false;

        if (!Helper.hasValue(props.lastSaveTimestamp)) {
            if (!Helper.hasValue(state.lastSaveTimestamp)) {
                lastSaveTimestamp = Date.now();
                createManager = true;
            }
        } else if (props.lastSaveTimestamp !== state.lastSaveTimestamp) {
            lastSaveTimestamp = props.lastSaveTimestamp;
            createManager = true;
        }

        let manager = licenseMangers[state.managerId];
        if (createManager) {
            const data: LicenseManagerInitData = {
                description: {},
                licenses: props.package.ContainedLicenses,
                userCount: props.numberOfUsers
            };

            props.package.ContainedLicenses.forEach(lic => {
                const modelLic = findLicenseById(props.model, lic.Id);
                data.description[lic.Id] = {
                    activationStatus: modelLic ? modelLic.activationStatus : ActivationStatus.Off,
                    currentUsage: props.licenseUsage[lic.Id],
                    capacity: lic.Amount,
                    allocations: lic.Allocations
                };
            });

            delete licenseMangers[state.managerId];
            manager = new LicenseManager(data);
        }

        return {
            lastSaveTimestamp,
            managerId: manager.getId(),
            renderData: manager.getLicenses().map(lic => {
                const packageLic = Helper.getArrayItem(props.package.ContainedLicenses, "Id", lic.id);
                const data: LicenseRenderInfo = {
                    id: lic.id,
                    count: packageLic.Amount,
                    currentLoad: packageLic.Allocations,
                    futureLoad: packageLic.Allocations + lic.usageChange,
                    usageChange: lic.usageChange,
                    expirationDate: packageLic.ExpirationDate,
                    state: lic.activeStatus,
                    canActivate: manager.canActivateLicense(lic.id),
                    canDeactivate: manager.canDeactivateLicense(lic.id),
                    dirty: lic.dirty
                };

                return data;
            })
        };
    }


    render() {
        //console.table(this.state.renderData);
        return (
            <table>
                <tbody>
                    {this.props.package.ContainedLicenses.map(this._renderLicense)}

                    {
                        Array.isArray(this.props.package.ExpiredLicenses) && this.props.package.ExpiredLicenses.length > 0 ?
                            <>
                                <tr>
                                    <td style={{ paddingTop: "20px", paddingBottom: "10px" }} colSpan={4}>{this.props.translations.expiredLicenses}</td>
                                </tr>
                                {this.props.package.ExpiredLicenses.map(this._renderLicense)}
                            </>
                            : null
                    }
                </tbody>
            </table>
        );
    }
}

export interface ModuleLicenseTableProps {
    package: CurrentLicensePackageInformation;
    model: Array<LicenseActivationStatus>;
    licenseUsage: IntKeyDictionary<number>;
    numberOfUsers: number;
    onChange: (modules: Array<LicenseActivationStatus>) => void;
    lastSaveTimestamp: number;
    onLicenseMouseEnter?: (license: ContainedLicense) => void;
    onLicenseMouseLeave?: (license: ContainedLicense) => void;
    onLicenseClick?: (license: ContainedLicense) => void;
    readonly: boolean;
    translations: {
        on: string;
        off: string;
        expiredLicenses
    }
}

export interface LicenseActivationStatus {
    id: number;
    activationStatus: ActivationStatus;
}

export enum ActivationStatus {
    On, Off, NotSet
}


export interface UserModuleAllocationProps {
    entity?: {
        RecordRight: {
            CanRead: boolean;
            CanWrite: boolean;
        },
        User: {
            Name: string;
        },
        ModuleRights: {
            ActiveModules: Array<number>
        }
    };
    onChange: (field: { model: string }, value: any, dataItem: any) => void;
    translations: StringKeyDictionary<string>;
}

interface ModuleLicenseTableState {
    lastSaveTimestamp: number;
    renderData: Array<LicenseRenderInfo>;
    managerId: string;
}

interface LicenseRenderInfo {
    id: number;
    state: ActivationStatus;
    expirationDate: string;
    count: number;
    currentLoad: number;
    futureLoad: number;
    usageChange: number;
    canActivate: boolean;
    canDeactivate: boolean;
    dirty: boolean;
}

interface UserModuleAllocationState {
    package: CurrentLicensePackageInformation;
    lastSaveTimestamp: number;
    licenseUsage: IntKeyDictionary<number>;
}

export interface CurrentLicensePackageInformation {
    Number: number;
    Name: string;
    Licensee: string;
    ExpirationDate: string;
    ContainedLicenses: Array<ContainedLicense>;
    ExpiredLicenses: Array<ContainedLicense>;
}

export interface ContainedLicense {
    Name: string;
    Amount: number;
    Id: number;
    CurrentLoad: number;
    Allocations: number;
    Expired: boolean;
    ExpirationDate: string;
}

